import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../Header';
import MobileHeader from '../MobileHeader';
import MobileMenu from '../MobileMenu';
import Footer from '../Footer';
import FloatingMenu from '../FloatingMenu';
import SEO from '../SEO';
import apiClient from '../../api/apiClient';
import {
  getAccessToken,
  getRefreshToken,
  removeTokens,
  setAccessToken,
} from '../../api/authUtils';
import { jwtDecode } from 'jwt-decode';
import '../styles/Header.css';
import './styles/BlogArticle.css';
import NewsletterSignup from '../NewsletterSignup';

const BlogArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFloatingMenu, setShowFloatingMenu] = useState(false);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch dell'articolo specifico dal backend
  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get(`/api/blog/articles/${slug}/`);
        setArticle(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Errore durante il recupero dell\'articolo:', err);
        setError('Impossibile caricare l\'articolo. Riprova più tardi.');
        setLoading(false);
      }
    };

    if (slug) {
      fetchArticle();
    }
  }, [slug]);

  // Controllo dello stato di autenticazione
  useEffect(() => {
    const checkAuthStatus = async () => {
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      if (accessToken) {
        try {
          const decodedAccessToken = jwtDecode(accessToken);
          const currentTime = Date.now() / 1000;

          if (decodedAccessToken.exp && decodedAccessToken.exp > currentTime) {
            setIsLoggedIn(true);
          } else if (refreshToken) {
            try {
              const response = await apiClient.post('/accounts/token/refresh/', {
                refresh: refreshToken,
              });

              if (response.status === 200) {
                const data = response.data;
                setAccessToken(data.access);
                setIsLoggedIn(true);
              } else {
                removeTokens();
                setIsLoggedIn(false);
              }
            } catch (error) {
              console.error('Errore durante il refresh del token:', error);
              removeTokens();
              setIsLoggedIn(false);
            }
          } else {
            removeTokens();
            setIsLoggedIn(false);
          }
        } catch (error) {
          console.error('Errore durante la decodifica del token di accesso:', error);
          removeTokens();
          setIsLoggedIn(false);
        }
      } else if (refreshToken) {
        try {
          const response = await apiClient.post('/accounts/token/refresh/', {
            refresh: refreshToken,
          });

          if (response.status === 200) {
            const data = response.data;
            setAccessToken(data.access);
            setIsLoggedIn(true);
          } else {
            removeTokens();
            setIsLoggedIn(false);
          }
        } catch (error) {
          console.error('Errore durante il refresh del token:', error);
          removeTokens();
          setIsLoggedIn(false);
        }
      } else {
        setIsLoggedIn(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Gestisce lo scroll per mostrare/nascondere il menu flottante
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowFloatingMenu(true);
      } else {
        setShowFloatingMenu(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Animazioni con Framer Motion
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  // Formatta la data in formato italiano
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('it-IT', options);
  };

  // Torna alla pagina principale del blog
  const handleBackToBlog = () => {
    navigate('/blog');
  };

  // Estrai la prima immagine dal contenuto HTML (se presente e l'articolo non ha cover_image)
  const extractFirstImageFromContent = (content) => {
    if (!content) return null;
    const matches = content.match(/<img[^>]+src="([^">]+)"/);
    return matches ? matches[1] : null;
  };

  // Determina l'immagine da usare per l'og:image
  const getArticleImage = () => {
    if (!article) return null;
    if (article.cover_image) return article.cover_image;
    return extractFirstImageFromContent(article.content);
  };

  // Pulisci la descrizione da tag HTML
  const cleanDescription = (htmlContent) => {
    if (!htmlContent) return '';
    // Rimuovi tag HTML e tronca a 160 caratteri
    const textContent = htmlContent.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return textContent.length > 160 ? textContent.substring(0, 157) + '...' : textContent;
  };

  return (
    <div className="blog-article-page">
      {article && (
        <SEO 
          title={article.title}
          description={article.meta_description || cleanDescription(article.content)}
          keywords={article.keywords || "blog, educazione, intelligenza artificiale, didattica"}
          author={article.author_name || "Minerva Education AI"}
          ogType="article"
          ogImage={getArticleImage()}
          ogUrl={`https://minervaeducationai.it/blog/${slug}`}
          canonical={`https://minervaeducationai.it/blog/${slug}`}
        />
      )}
    
      <Header
        isLoggedIn={isLoggedIn}
        setIsLoggedIn={setIsLoggedIn}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      {/* MOBILE HEADER */}
      <MobileHeader 
        isLoggedIn={isLoggedIn} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />

      {mobileMenuOpen && (
        <MobileMenu
          isLoggedIn={isLoggedIn}
          setIsLoggedIn={setIsLoggedIn}
          setMobileMenuOpen={setMobileMenuOpen}
        />
      )}

      {showFloatingMenu && (
        <FloatingMenu
          isLoggedIn={isLoggedIn}
          handleLogout={() => {
            removeTokens();
            setIsLoggedIn(false);
          }}
        />
      )}

      <div className="container">
        <main>
          <motion.article
            className="article-container"
            initial="hidden"
            animate="visible"
            variants={sectionVariants}
          >
            <button className="back-button" onClick={handleBackToBlog}>
              ← Torna al blog
            </button>

            {loading ? (
              <div className="article-loading">
                <p>Caricamento articolo...</p>
              </div>
            ) : error ? (
              <div className="article-error">
                <p>{error}</p>
              </div>
            ) : article ? (
              <>
                <header className="article-header">
                  <h1 className="article-title">{article.title}</h1>
                  <div className="article-meta">
                    <span className="article-date">{formatDate(article.publication_date)}</span>
                    {article.author_name && (
                      <div className="article-author">{article.author_name}</div>
                    )}
                  </div>
                </header>

                {article.cover_image && (
                  <div className="article-cover-image">
                    <img 
                      src={article.cover_image} 
                      alt={article.title} 
                    />
                  </div>
                )}

                <div 
                  className="article-content"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </>
            ) : (
              <div className="article-not-found">
                <p>Articolo non trovato.</p>
                <button className="back-button" onClick={handleBackToBlog}>
                  Torna alla pagina principale del blog
                </button>
              </div>
            )}
          </motion.article>
        </main>

        {/* Newsletter Form */}
        {!loading && article && (
          <motion.div 
            className="newsletter-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <div className="newsletter-container">
              <h3>Ti è piaciuto questo articolo?</h3>
              <p>Iscriviti alla nostra newsletter per ricevere aggiornamenti sui nuovi contenuti e risorse per la didattica.</p>
              
              <div className="newsletter-form">
                <NewsletterSignup />
              </div>
            </div>
          </motion.div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BlogArticle; 